import { useMutation, useQuery } from "react-query";
import {
  changePassword,
  createPassword,
  checkHeartBeat,
  deletePasskey,
  forgotPassword,
  generateOtp,
  getPlatformInfo,
  getSharedContentInfo,
  getUserInfo,
  getUserProfileData,
  googleAuthCallback,
  initiateRegister,
  listPasskeys,
  microsoftAuthCallback,
  passkeyRegisterBegin,
  passkeyRegisterFinish,
  refetchAccessToken,
  registerUser,
  registerWaitlist,
  resetPassword,
  sharedContentToken,
  signIn,
  signOut,
  updateUserInfo,
} from "../api/auth";
import { queryClient } from "./client";
import { useAuthStore } from "../store/useAuthStore";
import { CredentialResponse } from "@react-oauth/google";
import { randomSharedContent } from "./placeholderData";
import { PlatformEnum } from "../shared/utils";
import { UserType } from "../types/auth";

export type EmailOTPType = {
  email: string;
  userType: string;
};

export type NewPasswordType = {
  token: string;
  data: ResetPasswordType;
};

export type ResetPasswordType = {
  email: string;
  type: string;
  password: string;
  confirmPassword: string;
  transactionId: string;
  otp: string;
};

export type LastUsedProductType = {
  lastUsedProduct: PlatformEnum;
};
export type InitiateRegisterType = {
  email: string;
  fullName: string;
};

export type RegisterUserType = {
  email: string;
  password: string;
  confirmPassword: string;
  transactionId: string;
  otp: string;
  userType: UserType;
};

export function useSignIn() {
  return useMutation({
    mutationKey: "signIn",
    mutationFn: signIn,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useSignOut() {
  const { clear } = useAuthStore();
  return useMutation({
    mutationKey: "signOut",
    mutationFn: signOut,
    onSuccess: () => {
      sessionStorage.clear();
      queryClient.invalidateQueries();
      queryClient.clear();
      clear();
    },
    onError: () => {
      // Even if the server-side signOut fails (e.g. session already invalidated
      // by another tab), always clean up client state so the user is logged out
      // locally and the refetch-token loop cannot restart.
      sessionStorage.clear();
      queryClient.invalidateQueries();
      queryClient.clear();
      clear();
    },
  });
}

export function useCheckHeartBeat(enabled: boolean) {
  return useQuery({
    queryKey: "heartbeat",
    queryFn: checkHeartBeat,
    refetchInterval: 60000, // Refetch every 60 seconds (1 minute)
    refetchOnWindowFocus: "always", // Refetch when the tab regains focus
    staleTime: 0, // Ensure data is treated as stale immediately
    cacheTime: 0, // Avoid caching if unnecessary
    refetchOnMount: true,
    retryDelay: 10000,
  });
}

export function useRefetchAccessToken(enabled: boolean) {
  return useQuery({
    queryFn: refetchAccessToken,
    queryKey: "refetchAccessToken",
    enabled: enabled,
    retry: false,
  });
}

export function useGoogleAuthCallback(credential: string, enabled: boolean) {
  return useQuery({
    queryFn: googleAuthCallback,
    queryKey: ["googleAuthCallback", credential],
    enabled: enabled,
    retry: false,
  });
}
export function useMicrosoftAuthCallback(credential: string, enabled: boolean) {
  return useQuery({
    queryFn: microsoftAuthCallback,
    queryKey: ["microsoftAuthCallback", credential],
    enabled: enabled,
    retry: false,
  });
}

export function useGetUserInfo(email: string, enabled: boolean) {
  return useQuery({
    queryKey: ["getUserInfo", email],
    queryFn: getUserInfo,
    enabled: enabled,
    retry: false,
    cacheTime: 0,
    staleTime: 0,
  });
}

export function useUpdateUserInfo() {
  return useMutation({
    mutationKey: ["updateUserInfo"],
    mutationFn: updateUserInfo,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useGenerateOTP() {
  return useMutation({
    mutationKey: ["generateOtp"],
    mutationFn: generateOtp,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationKey: ["forgotPassword"],
    mutationFn: forgotPassword,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useResetPassword() {
  return useMutation({
    mutationKey: ["resetPassword"],
    mutationFn: resetPassword,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useChangePassword() {
  return useMutation({
    mutationKey: ["changePassword"],
    mutationFn: changePassword,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useCreatePassword() {
  return useMutation({
    mutationKey: ["createPassword"],
    mutationFn: createPassword,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useGetPlatformInfo(accesstoken: string, enabled?: boolean) {
  return useQuery({
    queryKey: ["getPlatformInfo", accesstoken],
    queryFn: getPlatformInfo,
    enabled: enabled,
  });
}
export function useGetSharedContentInfo(
  accesstoken: string,
  enabled?: boolean,
) {
  return useQuery({
    queryKey: ["getSharedContentInfo", accesstoken],
    queryFn: getSharedContentInfo,
    enabled: enabled,
    placeholderData: randomSharedContent(),
  });
}
export function useSharedContentToken() {
  return useMutation({
    mutationKey: ["sharedContentToken"],
    mutationFn: sharedContentToken,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useGetUserProfile() {
  return useQuery({
    queryKey: ["getSharedContentInfo"],
    queryFn: getUserProfileData,
  });
}

export function useInitiateRegister() {
  return useMutation({
    mutationKey: ["initiateRegister"],
    mutationFn: initiateRegister,
    onSuccess: () => {},
    onError: () => {},
  });
}
export function useRegisterUser() {
  return useMutation({
    mutationKey: ["registerUser"],
    mutationFn: registerUser,
    onSuccess: () => {},
    onError: () => {},
  });
}
export function useRegisterWaitlist() {
  return useMutation({
    mutationKey: "registerWaitlist",
    mutationFn: registerWaitlist,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useListPasskeys() {
  return useQuery({
    queryKey: "passkeys",
    queryFn: listPasskeys,
  });
}

export function useDeletePasskey() {
  return useMutation({
    mutationKey: ["deletePasskey"],
    mutationFn: (credentialId: string) => deletePasskey(credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries("passkeys");
    },
    onError: () => {},
  });
}

export function usePasskeyRegisterBegin() {
  return useMutation({
    mutationKey: ["passkeyRegisterBegin"],
    mutationFn: passkeyRegisterBegin,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function usePasskeyRegisterFinish() {
  return useMutation({
    mutationKey: ["passkeyRegisterFinish"],
    mutationFn: passkeyRegisterFinish,
    onSuccess: () => {},
    onError: () => {},
  });
}
